/**
 * Accelerated pairings — the virtual points a Swiss tournament hands its top half for
 * the first few rounds, so that a wide field stops producing 2100-vs-1400 in round 1.
 *
 * WHAT THIS FILE DOES, AND WHAT IT DELIBERATELY DOES NOT.
 * It computes a table of virtual points — one value per player per round — and nothing
 * else. It does not pair anything. The table is written into the pairing TRF as XXA
 * records (js/services/pairing-trf.js) and bbpPairings does the rest: it adds the value
 * for the round being paired to that player's score purely for the purpose of forming
 * score groups, and never to the score anyone is actually shown. That split is the whole
 * point — an accelerated tournament's standings are the real standings, and only the
 * pairing sees the virtual points.
 *
 * WHY THE ENGINE IS THE REFERENCE FOR "BAKU". The acceleration in `baku` below is ported
 * line for line from applyBakuAcceleration() in
 * vendor/bbpPairings/src/fileformats/trf.cpp (the `lastAcceleratedRank` /
 * `lastAccelerationRound` / `lastFullAccelerationRound` arithmetic, around line 708).
 * That is the same engine that will pair the round, so porting its own definition keeps
 * the app and the engine in agreement by construction rather than by hope. The engine
 * applies it itself only as the Burstein system's default; the Dutch system has no
 * default acceleration at all, which is why the values have to be written out as XXA.
 *
 * `classic` is not a port of anything — it is the plain, widely used acceleration that
 * most club opens mean when they say "accelerated": the top half of the field carries an
 * extra full point for the first N rounds, then nothing. It is spelled out in full by its
 * own description, so there is no external rule text to conform to and no way to get a
 * conformance claim wrong.
 *
 * RANKS ARE STARTING NUMBERS. "Top half" means starting numbers 1..ceil(N/2), because
 * that is the initial ranking order the app assigns by rating, and because acceleration
 * has to be decided from the field as it stands rather than from current scores. A player
 * added after round 1 lands at the end of the starting numbers and can therefore move the
 * half-way boundary by one place; the values for rounds already paired are history and are
 * unaffected, and only the round about to be paired is ever read.
 *
 * A classic <script> (window.Acceleration), like the engines beside it, so
 * js/services/pairing-trf.js can reach it without becoming a module. Pure: no DOM, no
 * state, no Firestore. Tests load it with tests/helpers/load-classic-script.js.
 */
(function (root) {
    'use strict';

    // The methods offered, in the order the settings form lists them.
    var METHODS = ['none', 'classic', 'baku'];

    var DEFAULTS = {
        method: 'none',
        // How many opening rounds `classic` accelerates. Two is the usual choice and the
        // one a club open means by "accelerated"; `baku` derives its own from the round
        // count and ignores this.
        rounds: 2
    };

    /**
     * Normalise anything shaped roughly like acceleration settings into a full object —
     * same contract as teamSettingsFrom/konradSettingsFrom in js/state.js, and for the
     * same reason: the create form, a new group and a loaded tournament must all read a
     * blank or hand-edited value identically, or a tournament would accelerate one round
     * and not the next.
     */
    function accelerationFrom(src) {
        var s = src || {};
        var n = parseInt(s.rounds, 10);
        return {
            method: METHODS.indexOf(s.method) >= 0 ? s.method : DEFAULTS.method,
            rounds: Number.isFinite(n) && n > 0 ? n : DEFAULTS.rounds
        };
    }

    /** Is any acceleration switched on? */
    function isActive(settings) {
        return accelerationFrom(settings).method !== 'none';
    }

    function pointValues(pointSystem) {
        var ps = String(pointSystem || '1-0.5-0').split('-').map(Number);
        return {
            win: Number.isFinite(ps[0]) ? ps[0] : 1,
            draw: Number.isFinite(ps[1]) ? ps[1] : 0.5,
            loss: Number.isFinite(ps[2]) ? ps[2] : 0
        };
    }

    /**
     * The Baku method is defined over a 1-½-0 scale and nothing else: it hands out a win's
     * worth of virtual points and then half a win's worth, which only means anything when a
     * draw is half a win and a loss is nothing. bbpPairings refuses the same combination
     * outright ("Point system is inconsistent with Baku acceleration method."), so refusing
     * here turns an engine error nobody can act on into a sentence naming the setting to
     * change.
     */
    function bakuSupports(pointSystem) {
        var v = pointValues(pointSystem);
        return v.loss === 0 && Math.abs(v.draw * 2 - v.win) < 1e-9 && v.win > 0;
    }

    /**
     * The virtual points for one method, as an array of rows indexed by RANK (row 0 is
     * starting number 1), each row holding one value per round in round order.
     *
     * Rows are only as long as they need to be — a player with no acceleration at all gets
     * an empty row — because a trailing zero and a missing entry mean the same thing to the
     * engine (`accelerations[roundIndex]`, zero past the end) and the shorter file is the
     * one a person can read.
     *
     * @param {Object} opts { playerCount, totalRounds, pointSystem, settings }
     * @returns {Array<Array<number>>} one row per rank, length === playerCount
     */
    function table(opts) {
        opts = opts || {};
        var playerCount = Math.max(0, parseInt(opts.playerCount, 10) || 0);
        var totalRounds = Math.max(0, parseInt(opts.totalRounds, 10) || 0);
        var settings = accelerationFrom(opts.settings);
        var pts = pointValues(opts.pointSystem);

        var rows = [];
        for (var i = 0; i < playerCount; i++) rows.push([]);
        if (settings.method === 'none' || !playerCount || !totalRounds) return rows;

        // The top half, rounded UP, is accelerated. Both methods agree on this, and the
        // integer division is bbpPairings' own: (N - 1) / 2 gives ranks 0..that, which is
        // ceil(N/2) players — 5 of 10, 6 of 11.
        var lastAcceleratedRank = Math.floor((playerCount - 1) / 2);

        if (settings.method === 'classic') {
            var accelRounds = Math.min(settings.rounds, totalRounds);
            for (var r = 0; r <= lastAcceleratedRank; r++) {
                for (var k = 0; k < accelRounds; k++) rows[r].push(pts.win);
            }
            return rows;
        }

        // Baku. Ported from applyBakuAcceleration() in
        // vendor/bbpPairings/src/fileformats/trf.cpp: the first half of the rounds are
        // accelerated, and the first half of THOSE get a whole win's worth rather than half.
        var lastAccelerationRound = Math.floor((totalRounds - 1) / 2);
        var lastFullAccelerationRound = Math.floor(lastAccelerationRound / 2);
        for (var rank = 0; rank <= lastAcceleratedRank; rank++) {
            var round = 0;
            for (; round <= lastFullAccelerationRound; round++) rows[rank].push(pts.win);
            for (; round <= lastAccelerationRound; round++) rows[rank].push(pts.draw);
        }
        return rows;
    }

    /**
     * The same table keyed by player id, which is what a caller holding app players wants.
     *
     * Players are ranked by starting number, so a player without one cannot be placed and
     * is left unaccelerated rather than being guessed at. Pairing refuses a field with
     * missing starting numbers long before this (see PairingTRF.build), so in practice
     * this only protects a preview drawn before they are assigned.
     *
     * @param {Array} players  app players ({ id, startNo })
     * @param {Object} opts    { totalRounds, pointSystem, settings }
     * @returns {Object} playerId -> array of per-round virtual points
     */
    function byPlayer(players, opts) {
        players = players || [];
        var ranked = players
            .filter(function (p) { return parseInt(p.startNo, 10) > 0; })
            .sort(function (a, b) { return parseInt(a.startNo, 10) - parseInt(b.startNo, 10); });

        var rows = table({
            playerCount: ranked.length,
            totalRounds: (opts || {}).totalRounds,
            pointSystem: (opts || {}).pointSystem,
            settings: (opts || {}).settings
        });

        var out = {};
        ranked.forEach(function (p, i) { out[p.id] = rows[i] || []; });
        return out;
    }

    /**
     * A one-line summary of what the current settings will do, for the settings form.
     * Templates with {placeholders} rather than spliced sentences, the same contract the
     * pairing refusals use, so a translator gets whole sentences.
     *
     * @returns {{ key: string, params: Object }|null} null when nothing is accelerated
     */
    function describe(settings, totalRounds, playerCount) {
        var s = accelerationFrom(settings);
        if (s.method === 'none') return null;

        var count = Math.max(0, parseInt(playerCount, 10) || 0);
        var rounds = Math.max(0, parseInt(totalRounds, 10) || 0);

        // A tournament being CREATED has no players yet, and that is exactly when an
        // arbiter is choosing this setting. Naming the rounds and saying "the top half"
        // still describes what will happen; going silent until a roster exists would leave
        // the choice unexplained at the only moment it is being made.
        var known = count > 0;
        var accelerated = known ? Math.floor((count - 1) / 2) + 1 : 0;

        if (s.method === 'classic') {
            var accelRounds = rounds ? Math.min(s.rounds, rounds) : s.rounds;
            return known
                ? {
                    key: 'The top {players} players carry an extra point for the first {rounds} rounds.',
                    params: { players: accelerated, rounds: accelRounds }
                }
                : {
                    key: 'The top half of the field carries an extra point for the first {rounds} rounds.',
                    params: { rounds: accelRounds }
                };
        }

        var lastAccelerationRound = Math.floor((rounds - 1) / 2);
        var lastFullAccelerationRound = Math.floor(lastAccelerationRound / 2);
        var spread = {
            full: rounds ? lastFullAccelerationRound + 1 : 0,
            half: rounds ? lastAccelerationRound + 1 : 0
        };
        return known
            ? {
                key: 'The top {players} players carry a full extra point for {full} rounds, then half a point through round {half}.',
                params: { players: accelerated, full: spread.full, half: spread.half }
            }
            : {
                key: 'The top half of the field carries a full extra point for {full} rounds, then half a point through round {half}.',
                params: spread
            };
    }

    root.Acceleration = {
        METHODS: METHODS,
        DEFAULTS: DEFAULTS,
        accelerationFrom: accelerationFrom,
        isActive: isActive,
        bakuSupports: bakuSupports,
        table: table,
        byPlayer: byPlayer,
        describe: describe
    };
})(typeof window !== 'undefined' ? window : globalThis);
